import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './Header.css';
import BReporter from '../../Media/Icons/BReporter.png';
import { User } from '../SinglePostHelper/SinglePostHelper';
import { useUser } from '../../UserContext';
import { useData } from '../../DataContext';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';

import { BACKEND_URL } from '../../config';

const FACTOMETER_SEEN_KEY = 'factometerSeen';

const NewTag = ({ className = '' }: { className?: string }) => (
	<span
		className={`inline-block px-1 rounded border border-blue-200 bg-blue-50 text-blue-600 text-[8px] font-semibold leading-3 tracking-wider pointer-events-none ${className}`}
	>
		NEW
	</span>
);

function Header() {
	const { user, set_user, isAdmin, isLoggedIn, setIsLoggedIn } = useUser();
	const { clearData } = useData();
	const [Nav, set_Nav] = useState<number>(-1);
	// "NEW" tag on the Fact-o-meter nav item until this browser opens the page once.
	const [showFactNew, setShowFactNew] = useState(() => {
		try {
			return !localStorage.getItem(FACTOMETER_SEEN_KEY);
		} catch {
			return false;
		}
	});
	// Cleared on click (a logged-out click redirects to /login before this header
	// renders on /fact-o-meter) and on visiting the page directly.
	const markFactSeen = () => {
		setShowFactNew(false);
		try {
			localStorage.setItem(FACTOMETER_SEEN_KEY, '1');
		} catch {
			/* storage blocked: the tag just shows again next visit */
		}
	};
	const navigate = useNavigate();
	const location = useLocation();
	const isRoutingRef = React.useRef(false);

	// Reactively set Nav based on current route
	useEffect(() => {
		const path = location.pathname;
		if (path === '/') {
			const options = {
				root: null,
				rootMargin: '-15% 0px -80% 0px',
				threshold: 0,
			};

			const callback = (entries: IntersectionObserverEntry[]) => {
				if (isRoutingRef.current) return;

				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (entry.target.classList.contains('home-scroll-point')) {
							set_Nav(0);
						} else if (entry.target.classList.contains('team-scroll-point')) {
							set_Nav(2);
						} else if (entry.target.classList.contains('feature-scroll-point')) {
							set_Nav(10);
						}
					}
				});
			};

			const observer = new IntersectionObserver(callback, options);

			// Handle navigation from other pages to specific sections
			const state = location.state as { targetNav?: number; targetSection?: string };
			if (state?.targetNav !== undefined && state?.targetSection) {
				set_Nav(state.targetNav);
				isRoutingRef.current = true;

				// Clear the state from history so refresh doesn't trigger it again
				navigate(location.pathname, { replace: true, state: {} });

				setTimeout(() => {
					scrollToSection(state.targetSection!);
					setTimeout(() => {
						isRoutingRef.current = false;
					}, 1000);
				}, 100);
			}

			// DOM might take a tick to render the sections after path changes
			setTimeout(() => {
				const homeSection = document.querySelector('.home-scroll-point');
				const teamSection = document.querySelector('.team-scroll-point');
				const featureSection = document.querySelector('.feature-scroll-point');

				if (homeSection) observer.observe(homeSection);
				if (teamSection) observer.observe(teamSection);
				if (featureSection) observer.observe(featureSection);
			}, 100);

			return () => observer.disconnect();
		} else if (path === '/news' || path.startsWith('/getpost/')) set_Nav(1);
		else if (path === '/explore') set_Nav(7);
		else if (path === '/login' || path === '/signup') set_Nav(3);
		else if (path === '/policy') set_Nav(4);
		else if (path === '/contact') set_Nav(5);
		else if (path === '/admin') set_Nav(6);
		else if (path === '/reports' || path.startsWith('/getreport/')) set_Nav(8);
		else if (path === '/profile') set_Nav(9);
		else if (path === '/fact-o-meter') {
			set_Nav(11);
			markFactSeen();
		}
		else set_Nav(-1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	// Dynamically track header height
	useEffect(() => {
		const headerEl = document.querySelector('header');
		if (!headerEl) return;

		const updateHeaderHeight = () => {
			document.documentElement.style.setProperty('--header-height', `${headerEl.offsetHeight}px`);
		};

		updateHeaderHeight(); // Initial set
		window.addEventListener('resize', updateHeaderHeight);
		return () => window.removeEventListener('resize', updateHeaderHeight);
	}, []);

	const handleLogout = (set_user: React.Dispatch<React.SetStateAction<User | null>>) => {
		// The auth cookie (sent via credentials:'include') identifies the user so the
		// server can revoke their token (bumpTokenVersion), clear the cookie, and log
		// the logout. No Authorization header needed now that auth is cookie-based.
		var requestOptions = {
			method: 'GET',
			redirect: 'follow' as RequestRedirect,
			credentials: 'include' as RequestCredentials,
		};

		fetch(`${BACKEND_URL}/auth/logout`, requestOptions)
			.then((response) => response.text())
			.then((result) => {
				localStorage.removeItem('isAuthed');
				clearData();
				set_user(null);
				setIsLoggedIn(false);
				const publicPages = ['/', '/contact', '/policy'];
				if (!publicPages.includes(location.pathname)) {
					setTimeout(() => {
						window.scrollTo(0, 0);
						navigate('/login');
					}, 0);
				}
			})
			.catch((error) => console.log('error', error));
	};

	const toggleMobileNav = () => {
		const navbar = document.getElementById('navbar');
		const overlay = document.getElementById('overlay');
		if (navbar && overlay) {
			const isOpening = !navbar.classList.contains('show');
			navbar.classList.toggle('show');
			overlay.classList.toggle('show');

			if (isOpening) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = 'auto';
			}
		}
	};

	const closeMobileNav = () => {
		const navbar = document.getElementById('navbar');
		const overlay = document.getElementById('overlay');
		if (navbar && overlay) {
			navbar.classList.remove('show');
			overlay.classList.remove('show');
			document.body.style.overflow = 'auto';
		}
	};

	const scrollToSection = (sectionClass: string) => {
		const section = document.getElementsByClassName(sectionClass)[0];
		const header = document.querySelector('header');
		const headerHeight = header ? header.getBoundingClientRect().height : 0;
		if (section) {
			const sectionTop = section.getBoundingClientRect().top + window.scrollY;
			window.scrollTo({
				top: sectionTop - headerHeight,
				behavior: 'smooth',
			});
		}
	};

	const handleNavClick = (navId: number, sectionClass: string) => {
		if (location.pathname !== '/') {
			navigate('/', { state: { targetNav: navId, targetSection: sectionClass } });
		} else {
			set_Nav(navId);
			isRoutingRef.current = true;
			scrollToSection(sectionClass);
			setTimeout(() => {
				isRoutingRef.current = false;
			}, 1000);
		}
	};

	const handleHomeClick = () => {
		handleNavClick(0, 'home-scroll-point');
	};

	const getNavClass = (isActive: boolean) => {
		return `h-10 px-3 rounded-md text-sm transition-all duration-300 flex items-center justify-center whitespace-nowrap gap-1.5 ${
			isActive
				? 'bg-blue-100/70 text-[#022c56] shadow-[0_0_15px_rgba(59,130,246,0.1)]'
				: 'text-gray-600 hover:bg-gray-100 hover:text-[#022c56]'
		}`;
	};

	const NavButton = ({
		label,
		isActive,
		onClick,
		isNew = false,
	}: {
		label: string;
		isActive: boolean;
		onClick: () => void;
		isNew?: boolean;
	}) => (
		<button className={`relative ${getNavClass(isActive)}`} onClick={onClick}>
			{isNew && <NewTag className='absolute -top-1.5 -right-1' />}
			<span className='flex flex-col items-center justify-center'>
				<span className='h-0 overflow-hidden invisible font-bold' aria-hidden='true'>
					{label}
				</span>
				<span className={isActive ? 'font-bold' : 'font-medium'}>{label}</span>
			</span>
		</button>
	);

	return (
		<>
			<header className='fixed top-0 left-0 right-0 z-[1001] bg-white shadow-lg'>
				{/* Mobile nav toggle */}
				<div className='lg:hidden relative flex items-center justify-between px-1.5 py-1.5'>
					<div className='z-10 flex-shrink-0'>
						<button
							onClick={toggleMobileNav}
							className='w-10 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors duration-200'
						>
							<FontAwesomeIcon icon={faBars} className='text-[#022c56] text-xl' />
						</button>
					</div>

					<div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center pointer-events-none'>
						<button className='flex items-center h-8 pointer-events-auto' onClick={handleHomeClick}>
							<img src={BReporter} className='h-8 w-8 mr-2 absolute right-full' alt='B-Reporter' />
							<span className='text-lg font-bold text-[#022c56] leading-none whitespace-nowrap'>B-Reporter</span>
						</button>
					</div>

					<div className='z-10 flex-shrink-0'>
						{user ? (
							<button
								className='w-20 py-1.5 text-[#022c56] hover:bg-gray-100 rounded-lg transition-colors duration-200 font-medium border border-[#022c56] text-sm'
								onClick={() => handleLogout(set_user)}
							>
								Logout
							</button>
						) : (
							<button
								className='w-20 py-1.5 bg-[#022c56] text-white rounded-lg hover:bg-[#034068] transition-colors duration-200 font-medium shadow-md text-sm'
								onClick={() => navigate('/login')}
							>
								Login
							</button>
						)}
					</div>
				</div>

				{/* Desktop navigation */}
				{/* Desktop navigation */}
				<div className='hidden lg:flex items-center justify-between px-4 py-2 relative'>
					{/* Left Section: Logo */}
					<button className='flex items-center gap-2' onClick={handleHomeClick}>
						<img src={BReporter} className='w-10 h-10' alt='B-Reporter' />
						<div className='text-[#022c56] text-left h-10 flex flex-col justify-center gap-y-0'>
							<h3 className='text-lg font-bold leading-tight m-0 p-0'>B-Reporter</h3>
							<p className='text-[10px] text-gray-600 leading-tight m-0 p-0'>Authentic Reporting</p>
						</div>
					</button>

					{/* Center Section: Pivot-based Navigation */}
					<div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center'>
						{/* Group 1: General (Attached to left of pivot) */}
						<div className='absolute right-full flex items-center mr-2 whitespace-nowrap'>
							<div className='flex items-center gap-1'>
								<NavButton label='Home' isActive={Nav === 0} onClick={handleHomeClick} />
								<NavButton
									label='Features'
									isActive={Nav === 10}
									onClick={() => handleNavClick(10, 'feature-scroll-point')}
								/>
								<NavButton label='Team' isActive={Nav === 2} onClick={() => handleNavClick(2, 'team-scroll-point')} />
							</div>
							<div className='w-[1px] h-6 bg-gray-300 ml-2'></div>
						</div>

						{/* Group 2: Auth-Required (The Centering Pivot) */}
						<div className='flex items-center gap-1 whitespace-nowrap'>
							<NavButton label='My Reports' isActive={Nav === 8} onClick={() => navigate('/reports')} />
							<NavButton label='My News' isActive={Nav === 1} onClick={() => navigate('/news')} />
							<NavButton label='Explore' isActive={Nav === 7} onClick={() => navigate('/explore')} />
							<NavButton
								label='Fact-o-meter'
								isActive={Nav === 11}
								isNew={showFactNew}
								onClick={() => {
									markFactSeen();
									navigate('/fact-o-meter');
								}}
							/>
						</div>

						{/* Group 3: Contact & Admin (Attached to right of pivot) */}
						<div className='absolute left-full flex items-center ml-2 whitespace-nowrap'>
							<div className='w-[1px] h-6 bg-gray-300 mr-2'></div>
							<div className='flex items-center gap-1'>
								<NavButton label='Contact' isActive={Nav === 5} onClick={() => navigate('/contact')} />
								{isAdmin && isLoggedIn && (
									<>
										<div className='w-[1px] h-6 bg-gray-300 mx-1'></div>
										<NavButton label='Admin Panel' isActive={Nav === 6} onClick={() => navigate('/admin')} />
									</>
								)}
							</div>
						</div>
					</div>

					{/* Right Section: Auth Button */}
					<div className='flex items-center gap-4'>
						{user ? (
							<button
								className='w-24 h-10 px-4 text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200 font-medium border border-gray-300 text-sm flex items-center justify-center'
								onClick={() => handleLogout(set_user)}
							>
								Logout
							</button>
						) : (
							<button
								className='w-24 h-10 px-4 bg-[#022c56] text-white rounded-md hover:bg-[#034068] transition-colors duration-200 font-medium text-sm flex items-center justify-center shadow-sm'
								onClick={() => navigate('/login')}
							>
								Login
							</button>
						)}
					</div>
				</div>
			</header>

			{/* Mobile Navigation Menu */}
			<nav
				id='navbar'
				className='lg:hidden fixed top-0 left-0 w-[229px] h-full bg-white shadow-2xl transform -translate-x-full transition-transform duration-300 z-[1100] opacity-100 flex flex-col'
			>
				<div className='flex-grow overflow-y-auto p-4'>
					<div className='flex items-center justify-between mb-4'>
						<div className='flex items-center h-8 gap-2'>
							<img src={BReporter} className='h-8 w-8' alt='B-Reporter' />
							<span className='text-lg font-bold text-[#022c56] whitespace-nowrap leading-none'>B-Reporter</span>
						</div>
						<button onClick={closeMobileNav} className='p-1.5 hover:bg-gray-100 rounded-lg'>
							<FontAwesomeIcon icon={faTimes} className='text-[#022c56]' />
						</button>
					</div>

					<div className='flex flex-col gap-1'>
						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 0
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								handleHomeClick();
								closeMobileNav();
							}}
						>
							Home
						</button>

						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 10
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								handleNavClick(10, 'feature-scroll-point');
								closeMobileNav();
							}}
						>
							Features
						</button>

						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 2
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								handleNavClick(2, 'team-scroll-point');
								closeMobileNav();
							}}
						>
							Team
						</button>

						<div className='my-1 border-b border-[#022c56]/30 mx-2' />

						{/* Grouped authentication-required mobile pages */}
						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 8
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								navigate('/reports');
								closeMobileNav();
							}}
						>
							My Reports
						</button>

						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 1
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								navigate('/news');
								closeMobileNav();
							}}
						>
							My News
						</button>

						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 7
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								navigate('/explore');
								closeMobileNav();
							}}
						>
							Explore
						</button>

						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 11
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								markFactSeen();
								navigate('/fact-o-meter');
								closeMobileNav();
							}}
						>
							Fact-o-meter
							{showFactNew && <NewTag className='ml-2 align-middle' />}
						</button>

						<div className='my-1 border-b border-[#022c56]/30 mx-2' />

						<button
							className={`w-full text-left px-2 py-2 font-semibold transition-all duration-200 ${
								Nav === 5
									? 'text-[#022c56] underline underline-offset-4 decoration-2'
									: 'text-gray-500 hover:text-[#022c56]'
							}`}
							onClick={() => {
								navigate('/contact');
								closeMobileNav();
							}}
						>
							Contact
						</button>
					</div>
				</div>
				<div className='p-6 mt-auto border-t border-gray-50 text-center'>
					<p className='text-xs text-gray-400 font-medium'>© 2025 B-Reporter</p>
				</div>
			</nav>

			{/* Mobile overlay */}
			<div
				id='overlay'
				className='lg:hidden fixed inset-0 bg-black/50 opacity-0 invisible transition-all duration-300 z-[1050]'
				onClick={closeMobileNav}
			></div>
		</>
	);
}

export default Header;
